import React, { useRef, useEffect, useState, MutableRefObject } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Game } from "../types/Game";
import { url } from "../api/url";
interface ScrollableGameSectionProps {
  title: string;
  games: Game[];
  viewAllLink?: () => void;
  sectionType?: "default" | "featured";
  onCardClick?: (game: Game) => void;
  refObj?: MutableRefObject<HTMLDivElement | null>;
  scrollHandler?: (dir: "left" | "right") => void;
  canScrollLeft?: boolean;
  canScrollRight?: boolean;
}

const ScrollableGameSection: React.FC<ScrollableGameSectionProps> = ({
  title,
  games = [],
  viewAllLink,
  sectionType = "default",
  onCardClick,
  refObj,
  scrollHandler,
  canScrollLeft = false,
  canScrollRight = false,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  return (
    <section className="mb-8">
      <div className="flex justify-between items-center mb-3 px-4 sm:px-10">
        <h2 className="text-lg text-[#ffffff] font-bold">{title}</h2>
        {viewAllLink && (
          <button
            className="text-sm text-[#f92c2c] hover:underline"
            onClick={viewAllLink}
          >
            {t("common.viewAll")}
          </button>
        )}
      </div>

      <div className="relative w-full overflow-hidden">
        {/* Left Gradient */}
        {canScrollLeft && (
          <div
            className={`pointer-events-none absolute top-0 ${isRTL ? "right-0" : "left-0"
              } h-full w-24 z-10 bg-gradient-to-${isRTL ? "l" : "r"
              } from-[#070B28] to-transparent`}
          />
        )}

        {/* Right Gradient */}
        {canScrollRight && (
          <div
            className={`pointer-events-none absolute top-0 ${isRTL ? "left-0" : "right-0"
              } h-full w-24 z-10 bg-gradient-to-${isRTL ? "r" : "l"
              } from-[#070B28] to-transparent`}
          />
        )}

        {/* Scroll Left Button */}
        {canScrollLeft && scrollHandler && (
          <button
            onClick={() => scrollHandler(isRTL ? "right" : "left")}
            className={`absolute ${isRTL ? "right-2" : "left-2"
              } top-1/2 transform -translate-y-1/2 z-20 bg-black bg-opacity-50 p-2 rounded-full text-white hover:bg-opacity-80`}
          >
            <FaChevronLeft />
          </button>
        )}

        {/* Game Cards */}
        <div
          ref={refObj}
          className="flex overflow-x-auto no-scrollbar gap-4 px-4 sm:px-10"
        >
          {games.map((game, index) => {
            const number = index + 1;
            const isDoubleDigit = number >= 10;

            return (
              <div
                key={index}
                onClick={() => onCardClick?.(game)}
                className="rounded-lg overflow-hidden bg-gray-800 w-[140px] sm:w-[18rem] h-[120px] sm:h-[160px] flex-shrink-0 cursor-pointer transition-transform duration-300 transform hover:scale-105 hover:ring-2 hover:ring-white hover:ring-opacity-0 addsquarebox"
              >
                <img
                  src={game.thumbImage?.default ? `${url.imgUrl}/${game.thumbImage.default}` : game.image}
                  alt={game.title || game.name}
                  className="addsquarebox rounded-lg"
                />

                {/* {sectionType === "featured" && (
                  <span
                    className="absolute bottom-0 left-[10px] sm:left-[20px] text-[100px] sm:text-[185px] font-bold leading-[0.77] tracking-[-0.15em] text-white z-[-1] opacity-90 font-roboto transition-all duration-300 group-hover:drop-shadow-[0_0_15px_#00FFFF]"
                    style={{
                      textShadow:
                        "1px 1px 0 #070B28, -1px 1px 0 #070B28, -1px -1px 0 #070B28, 1px -1px 0 #070B28, 2px 2px 0 #070B28, -2px -2px 0 #070B28",
                      fontWeight: "800",
                    }}
                  >
                    {number}
                  </span>
                )} */}
              </div>
            );
          })}
        </div>

        {/* Scroll Right Button */}
        {canScrollRight && scrollHandler && (
          <button
            onClick={() => scrollHandler(isRTL ? "left" : "right")}
            className={`absolute ${isRTL ? "left-2" : "right-2"
              } top-1/2 transform -translate-y-1/2 z-20 bg-black bg-opacity-50 p-2 rounded-full text-white hover:bg-opacity-80`}
          >
            <FaChevronRight />
          </button>
        )}
      </div>
    </section>
  );
};

export default ScrollableGameSection;
